import { getApiClient } from './client'
import type {
  TagResponseDto,
  TagListResponseDto,
  CreateTagDto,
  UpdateTagDto,
} from './generated/api'

/**
 * Tags API Facade
 *
 * Type-safe wrapper around the generated API client for tags operations.
 * Provides consistent error handling and cleaner API for React Query hooks.
 */

/**
 * Fetch all tags with optional filters
 *
 * @param params - Query parameters for filtering
 * @param params.userId - Filter by user ID
 * @param params.name - Filter by exact tag name
 * @param params.searchText - Search tags by name (case-insensitive)
 * @param params.limit - Maximum number of results (default: 20)
 * @param params.offset - Number of results to skip (default: 0)
 * @param params.includeRelations - Include related taggings data
 * @returns Promise resolving to list of tags with pagination metadata
 */
export async function fetchTags(params?: {
  userId?: string
  name?: string
  searchText?: string
  limit?: number
  offset?: number
  includeRelations?: boolean
}): Promise<TagListResponseDto> {
  const response = await getApiClient().api.tagsControllerFindAll({
    userId: params?.userId,
    name: params?.name,
    searchText: params?.searchText,
    limit: params?.limit,
    offset: params?.offset,
    includeRelations: params?.includeRelations,
  })

  return response.data
}

/**
 * Search tags by name
 *
 * @param query - Search query for tag names
 * @param limit - Maximum number of results (default: 20)
 * @returns Promise resolving to array of matching tags
 */
export async function searchTags(
  query: string,
  limit?: number
): Promise<TagResponseDto[]> {
  const response = await getApiClient().api.tagsControllerSearch({
    q: query,
    limit,
  })

  return response.data
}

/**
 * Fetch a single tag by ID
 *
 * @param id - Tag ID (UUID)
 * @returns Promise resolving to tag details
 */
export async function fetchTag(id: string): Promise<TagResponseDto> {
  const response = await getApiClient().api.tagsControllerFindById(id)

  return response.data
}

/**
 * Create a new tag
 *
 * @param data - Tag data to create
 * @returns Promise resolving to created tag
 */
export async function createTag(data: CreateTagDto): Promise<TagResponseDto> {
  const response = await getApiClient().api.tagsControllerCreate(data)

  return response.data
}

/**
 * Update an existing tag
 *
 * @param id - Tag ID (UUID)
 * @param data - Partial tag data to update
 * @returns Promise resolving to updated tag
 */
export async function updateTag(
  id: string,
  data: UpdateTagDto
): Promise<TagResponseDto> {
  const response = await getApiClient().api.tagsControllerUpdate(id, data)

  return response.data
}

/**
 * Delete a tag (only if not in use)
 *
 * @param id - Tag ID (UUID)
 * @returns Promise resolving to void on success
 */
export async function deleteTag(id: string): Promise<void> {
  await getApiClient().api.tagsControllerDelete(id)
}

/**
 * Get tag statistics
 *
 * @returns Promise resolving to tag statistics (total tags)
 */
export async function getTagStatistics(): Promise<{ totalTags?: number }> {
  const response = await getApiClient().api.tagsControllerGetStatistics()

  return response.data
}
